import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { getToken, setToken } from "../lib/authToken";
import { CloudNavBar } from "../components/CloudNavBar";

interface Member {
  tenant_id: string;
  user_id: string;
  email: string;
  name: string;
  role: string;
  invited_at: string;
  accepted_at: string | null;
}

interface MembersResponse {
  members: Member[];
  max_users: number;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan_id: string;
  status: string;
}

export default function TeamPage() {
  const [members, setMembers] = useState<Member[]>([]);
  const [maxUsers, setMaxUsers] = useState(0);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [currentRole, setCurrentRole] = useState("");
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenantId, setCurrentTenantId] = useState("");

  const loadMembers = useCallback(() => {
    api.get<MembersResponse>("/tenants/current/members")
      .then((r) => {
        setMembers(r.members || []);
        setMaxUsers(r.max_users || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadMembers();

    // Decode JWT to get current user's role and tenant
    try {
      const token = getToken();
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setCurrentRole(payload.role || "");
        setCurrentTenantId(payload.tenant_id || "");
      }
    } catch { /* ignore */ }

    // Load user's tenants for switcher
    api.get<{ tenants: Tenant[] }>("/auth/user-tenants")
      .then((r) => setTenants(r.tenants || []))
      .catch(() => {});
  }, [loadMembers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError("");
    setInviteSuccess("");
    try {
      await api.post("/tenants/current/invite", { email: inviteEmail, role: inviteRole });
      setInviteSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteRole("member");
      loadMembers();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Failed to invite");
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await api.put(`/tenants/current/members/${userId}/role`, { role: newRole });
      loadMembers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update role");
    }
  };

  const handleRemove = async (userId: string) => {
    try {
      await api.del(`/tenants/current/members/${userId}`);
      setConfirmRemove(null);
      loadMembers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to remove member");
    }
  };

  const handleSwitchTenant = async (tenantId: string) => {
    try {
      const res = await api.post<{ token: string }>("/auth/switch-tenant", { tenant_id: tenantId });
      setToken(res.token);
      window.location.reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to switch tenant");
    }
  };

  const handleResendInvite = async (userId: string, email: string) => {
    setResendingUserId(userId);
    setResendSuccess(null);
    try {
      await api.post(`/tenants/current/members/${userId}/resend-invite`, {});
      setResendSuccess(`Invite resent to ${email}`);
      setTimeout(() => setResendSuccess(null), 5000);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to resend invite");
    } finally {
      // Disable button for 10 seconds to prevent spam
      setTimeout(() => setResendingUserId(null), 10000);
    }
  };

  const isOwner = currentRole === "owner";
  const isAdminOrOwner = currentRole === "owner" || currentRole === "admin";
  const unlimited = maxUsers < 0;

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      <div className="max-w-3xl mx-auto py-8 px-6">
      <h1 className="text-2xl font-bold mb-6">Team Members</h1>

      {/* Tenant Switcher */}
      {tenants.length > 1 && (
        <label className="block bg-card rounded-lg shadow p-4 mb-6">
          <span className="block text-sm font-medium text-muted-foreground mb-2">Switch Workspace</span>
          <select
            value={currentTenantId}
            onChange={(e) => handleSwitchTenant(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm bg-background"
          >
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>
      )}

      {/* Quota Indicator */}
      <div className="bg-card rounded-lg shadow p-4 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Team members</span>
          <span className="font-medium">
            {members.length} / {unlimited ? "Unlimited" : maxUsers}
          </span>
        </div>
        {!unlimited && maxUsers > 0 && (
          <div className="h-2 bg-muted rounded-full overflow-hidden mt-2">
            <div
              className={`h-full rounded-full ${
                members.length / maxUsers >= 0.9 ? "bg-red-500" :
                members.length / maxUsers >= 0.7 ? "bg-yellow-500" : "bg-blue-500"
              }`}
              style={{ width: `${Math.min((members.length / maxUsers) * 100, 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Invite Form */}
      {isAdminOrOwner && (
        <div className="bg-card rounded-lg shadow p-4 mb-6">
          <h2 className="text-sm font-medium mb-3">Invite Team Member</h2>
          {inviteError && <p className="text-red-600 text-sm mb-2">{inviteError}</p>}
          {inviteSuccess && <p className="text-green-600 text-sm mb-2">{inviteSuccess}</p>}
          <form onSubmit={handleInvite} className="flex gap-2 items-end">
            <div className="flex-1">
              <input
                type="email"
                placeholder="Email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                className="w-full border rounded px-3 py-2 text-sm bg-background"
              />
            </div>
            <div>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="border rounded px-3 py-2 text-sm bg-background"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              type="submit"
              className="bg-primary text-primary-foreground rounded px-4 py-2 text-sm hover:opacity-90 whitespace-nowrap"
            >
              Invite
            </button>
          </form>
        </div>
      )}

      {/* Resend Success Toast */}
      {resendSuccess && (
        <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-6">
          <p className="text-green-700 dark:text-green-300 text-sm">{resendSuccess}</p>
        </div>
      )}

      {/* Member List */}
      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Joined</th>
                {isAdminOrOwner && <th className="px-4 py-3 font-medium w-10"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.map((m) => (
                <tr key={m.user_id}>
                  <td className="px-4 py-3">{m.name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{m.email || m.user_id}</td>
                  <td className="px-4 py-3">
                    {isOwner && m.role !== "owner" ? (
                      <select
                        value={m.role}
                        onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                        className="border rounded px-2 py-1 text-sm bg-background"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className="capitalize">{m.role}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {m.accepted_at ? (
                      <span className="text-green-600 text-xs font-medium bg-green-50 dark:bg-green-950 px-2 py-0.5 rounded">Active</span>
                    ) : (
                      <span className="text-yellow-600 text-xs font-medium bg-yellow-50 dark:bg-yellow-950 px-2 py-0.5 rounded">Invited</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {m.accepted_at
                      ? new Date(m.accepted_at).toLocaleDateString()
                      : new Date(m.invited_at).toLocaleDateString()
                    }
                  </td>
                  {isAdminOrOwner && (
                    <td className="px-4 py-3">
                      {m.role !== "owner" && (
                        <div className="flex gap-2 items-center">
                          {!m.accepted_at && (
                            <button
                              onClick={() => handleResendInvite(m.user_id, m.email)}
                              disabled={resendingUserId === m.user_id}
                              className="text-blue-600 hover:text-blue-800 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Resend invite"
                            >
                              {resendingUserId === m.user_id ? "Sent" : "Resend"}
                            </button>
                          )}
                          {confirmRemove === m.user_id ? (
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleRemove(m.user_id)}
                                className="text-red-600 text-xs font-medium hover:underline"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setConfirmRemove(null)}
                                className="text-muted-foreground text-xs hover:underline"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmRemove(m.user_id)}
                              className="text-red-500 hover:text-red-700 text-xs"
                              title="Remove member"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <td colSpan={isAdminOrOwner ? 6 : 5} className="px-4 py-6 text-center text-muted-foreground">
                    No team members yet. Invite someone to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
}
